import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight } from 'lucide-react';

interface SubcategorySelectorProps {
  domainKey: string | null;
  onSelect: (subcategoryKey: string) => void;
  onSkip: () => void;
}

interface ServiceSubcategory {
  id: string;
  subcategory_key: string;
  name_en: string;
  name_zh: string;
  description_en: string | null;
  description_zh: string | null;
  domain_id: string | null;
  sort_order: number;
}

const SubcategorySelector: React.FC<SubcategorySelectorProps> = ({ 
  domainKey, 
  onSelect, 
  onSkip 
}) => {
  const { t, i18n } = useTranslation('service-selection');
  const isZh = i18n.language === 'zh';

  const { data: subcategories, isLoading, error } = useQuery({
    queryKey: ['service-subcategories', domainKey],
    queryFn: async () => {
      let query = supabase
        .from('service_subcategories')
        .select('*, effective_domains!inner(domain_key, sort_order)')
        .eq('is_active', true);

      // If domain is specified AND not 'all_domains', filter by it
      if (domainKey && domainKey !== 'all_domains') {
        query = query.eq('effective_domains.domain_key', domainKey);
      }

      const { data, error } = await query
        .order('sort_order', { foreignTable: 'effective_domains', ascending: true })
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // Client-side stable sorting as fallback (身→灵→意→境 order)
      const domainOrder = ['body', 'vitality', 'consciousness', 'context'];
      const sorted = (data || []).sort((a: any, b: any) => {
        const da = a.effective_domains?.domain_key;
        const db = b.effective_domains?.domain_key;
        const ia = domainOrder.indexOf(da);
        const ib = domainOrder.indexOf(db);
        if (ia !== ib) return ia - ib;
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      });

      return sorted as ServiceSubcategory[];
    },
    enabled: true // Always fetch, even if domainKey is null
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load subcategories</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-center text-muted-foreground mb-6">
        {t('selectSubcategory')}
        {(!domainKey || domainKey === 'all_domains') && (
          <span className="block text-xs mt-1 text-muted-foreground/70">
            {isZh ? '（已按身→灵→意→境排序）' : '(Sorted by Body→Vitality→Consciousness→Context)'}
          </span>
        )}
      </p>

      <div className="space-y-3 max-w-3xl mx-auto">
        {subcategories?.map((subcategory) => (
          <button
            key={subcategory.id}
            onClick={() => onSelect(subcategory.subcategory_key)}
            className="w-full p-4 rounded-lg border-2 border-border bg-card hover:border-primary hover:shadow-md transition-all duration-300 hover:translate-x-2 text-left group"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                  {isZh ? subcategory.name_zh : subcategory.name_en}
                </h3>
                {(subcategory.description_en || subcategory.description_zh) && (
                  <p className="text-sm text-muted-foreground">
                    {isZh ? subcategory.description_zh : subcategory.description_en}
                  </p>
                )}
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-center pt-4">
        <Button variant="outline" onClick={onSkip} size="lg">
          {t('skipButton')}
        </Button>
      </div>
    </div>
  );
};

export default SubcategorySelector;
